import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, CreditCard, Check, CheckCircle, MessageCircle, Star, X, Calendar, MapPin, Clock, AlertTriangle, QrCode } from 'lucide-react';
import { BookingChat } from '../components/BookingChat';
import { QRCodeSVG } from 'qrcode.react';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';

export default function BookingDetailPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user, api, token } = useAuth();
  const { language } = useLanguage();

  const [booking, setBooking] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentType, setPaymentType] = useState('full');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [cancelReason, setCancelReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [bookingRes, reviewRes] = await Promise.all([
        api().get(`/bookings/${bookingId}`),
        api().get(`/reviews/booking/${bookingId}`).catch(() => ({ data: null }))
      ]);
      setBooking(bookingRes.data);
      setReview(reviewRes.data);
      setPaymentType(bookingRes.data.payment_type);
    } catch (error) {
      toast.error(language === 'en' ? 'Booking not found' : 'Reserva no encontrada');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [bookingId, api, language, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePayment = async () => {
    setProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      const currentPaymentType = booking.status === 'partially_paid' ? 'remaining' : paymentType;
      const response = await api().post(
        `/payments/create-session?booking_id=${bookingId}&origin_url=${originUrl}&payment_type=${currentPaymentType}`
      );
      
      if (response.data.url) {
        // Open Stripe in a new tab so the embedded WebView in some environments
        // doesn't trigger Google's `disallowed_useragent` block (403).
        const win = window.open(response.data.url, '_blank', 'noopener,noreferrer');
        if (!win) {
          // Pop-up blocked → fall back to same-tab navigation
          window.location.href = response.data.url;
        }
        setProcessingPayment(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Error' : 'Error'));
      setProcessingPayment(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewData.comment.trim()) {
      toast.error(language === 'en' ? 'Please write a comment' : 'Por favor escribe un comentario');
      return;
    }

    setSubmitting(true);
    try {
      await api().post('/reviews', {
        booking_id: bookingId,
        rating: reviewData.rating,
        comment: reviewData.comment
      });
      toast.success(language === 'en' ? 'Review submitted!' : '¡Reseña enviada!');
      setShowReviewDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Error' : 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      await api().post(`/bookings/${bookingId}/cancel?reason=${encodeURIComponent(cancelReason)}`);
      toast.success(language === 'en' ? 'Booking cancelled' : 'Reserva cancelada');
      setShowCancelDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Error' : 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const phone = booking?.provider_phone?.replace(/\D/g, '') || '';
    const message = encodeURIComponent(
      language === 'en'
        ? `Hi! I made a booking through BidTrip. Booking ID: ${booking.id}`
        : `¡Hola! Hice una reserva a través de BidTrip. ID de reserva: ${booking.id}`
    );
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const getCategoryName = (id) => {
    const names = {
      yacht: language === 'en' ? 'Yacht' : 'Yate',
      tour: 'Tour',
      activity: language === 'en' ? 'Activity' : 'Actividad',
      party: language === 'en' ? 'Party' : 'Fiesta',
      car_rental: language === 'en' ? 'Car Rental' : 'Renta de Auto',
      lodging: language === 'en' ? 'Lodging' : 'Hospedaje'
    };
    return names[id] || id;
  };

  const getLocationName = (id) => {
    const names = {
      playa_del_carmen: 'Playa del Carmen',
      cancun: 'Cancún',
      tulum: 'Tulum',
      quintana_roo: 'Quintana Roo',
      los_cabos: 'Los Cabos'
    };
    return names[id] || id;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-caribbean"></div>
      </div>
    );
  }

  if (!booking) return null;

  const getStatusColor = (status) => {
    const colors = {
      pending_payment: 'bg-rosa text-white',
      partially_paid: 'bg-gold text-foreground',
      confirmed: 'bg-caribbean text-white',
      completed: 'bg-foreground text-white',
      cancelled: 'bg-destructive text-white'
    };
    return colors[status] || 'bg-muted';
  };

  const canCancel = ['pending_payment', 'partially_paid', 'confirmed'].includes(booking.status);
  const canReview = ['confirmed', 'completed'].includes(booking.status) && !review && user?.role === 'tourist';
  const canChat = ['confirmed', 'partially_paid', 'completed'].includes(booking.status);
  const isPaid = booking.payment_status === 'paid' || booking.status === 'confirmed';
  const canComplete = ['confirmed', 'partially_paid'].includes(booking.status) && booking.payment_status === 'paid';
  const isCompleted = booking.status === 'completed';

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      await api().post(`/bookings/${bookingId}/complete`);
      toast.success(language === 'en' ? 'Experience marked as completed!' : '¡Experiencia marcada como completada!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Error' : 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileShell>
      <TopBar
        showBrand={false}
        title={language === 'en' ? 'Booking' : 'Reserva'}
        right={
          <button onClick={() => navigate('/dashboard')} className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
            <ArrowLeft size={18} />
          </button>
        }
      />
      <div className="px-4 py-4" data-testid="booking-detail-page">
        <div className="space-y-4">

        <div className="bg-card rounded-2xl shadow-xl overflow-hidden mb-6">
          {/* Header */}
          <div className="bg-caribbean p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="font-heading text-xl font-bold">{getCategoryName(booking.category)}</h1>
                <div className="flex items-center gap-1 text-white/80 text-sm">
                  <MapPin size={14} />
                  <span>{getLocationName(booking.location)}</span>
                </div>
              </div>
              <Badge className={getStatusColor(booking.status)}>
                {booking.status === 'pending_payment' && (language === 'en' ? 'Pending Payment' : 'Pendiente de Pago')}
                {booking.status === 'partially_paid' && (language === 'en' ? 'Deposit Paid' : 'Apartado')}
                {booking.status === 'confirmed' && (language === 'en' ? 'Confirmed' : 'Confirmado')}
                {booking.status === 'completed' && (language === 'en' ? 'Completed' : 'Completado')}
                {booking.status === 'cancelled' && (language === 'en' ? 'Cancelled' : 'Cancelado')}
              </Badge>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-white/80">
              <div className="flex items-center gap-1">
                <Calendar size={14} />
                <span>{booking.date}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock size={14} />
                <span>{booking.time}</span>
              </div>
            </div>
          </div>

          {/* Provider Info */}
          {booking.provider_name && (
            <div className="p-4 border-b">
              <p className="text-sm text-muted-foreground">{language === 'en' ? 'Provider' : 'Proveedor'}</p>
              <Link 
                to={`/provider/${booking.provider_id}`}
                className="font-semibold text-caribbean hover:underline"
              >
                {booking.provider_name}
              </Link>
            </div>
          )}

          {/* Price Breakdown - NEW model */}
          <div className="p-6 space-y-3">
            <div className="flex items-center justify-between py-2">
              <span className="text-muted-foreground text-sm">{language === 'en' ? 'Experience total' : 'Total experiencia'}</span>
              <span className="font-heading text-xl font-bold">${booking.total_price}</span>
            </div>

            {booking.reservation_fee != null && (
              <>
                <div className="flex items-center justify-between py-2 bg-caribbean/10 px-3 rounded-xl">
                  <div className="text-sm">
                    <p className="font-semibold text-caribbean">{language === 'en' ? 'Reservation (BidTrip)' : 'Reserva (BidTrip)'}</p>
                    <p className="text-[11px] text-muted-foreground">17% + IVA</p>
                  </div>
                  <span className="font-heading text-lg font-bold text-caribbean">${booking.reservation_fee.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between py-2 bg-rosa/10 px-3 rounded-xl">
                  <div className="text-sm">
                    <p className="font-semibold text-rosa">
                      {user?.role === 'provider'
                        ? (language === 'en' ? 'You collect on arrival' : 'Cobras al llegar')
                        : (language === 'en' ? 'Pay to provider on arrival' : 'Pagas al proveedor al llegar')}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{language === 'en' ? 'Cash or as agreed' : 'En efectivo o como acuerden'}</p>
                  </div>
                  <span className="font-heading text-lg font-bold text-rosa">${(booking.provider_balance_due ?? (booking.total_price - booking.reservation_fee)).toFixed(2)}</span>
                </div>
              </>
            )}

            {booking.amount_paid > 0 && (
              <div className="flex items-center justify-between py-1 text-caribbean text-sm">
                <span>{language === 'en' ? 'Reservation paid' : 'Reserva pagada'}</span>
                <span className="font-semibold">${booking.amount_paid.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* QR Code for confirmed bookings */}
          {isPaid && booking.qr_code && (
            <div className="p-6 border-t text-center">
              <p className="text-sm text-muted-foreground mb-3">
                {language === 'en' ? 'Your Ticket QR Code' : 'Tu Código QR'}
              </p>
              <div className="inline-block p-4 bg-card border-2 border-caribbean rounded-xl">
                <QRCodeSVG 
                  value={booking.qr_code}
                  size={120}
                  level="H"
                />
              </div>
              <p className="mt-2 font-mono text-xs text-muted-foreground">{booking.qr_code}</p>
              
              {/* Send to WhatsApp button */}
              <Button
                onClick={() => {
                  const ticketText = `🎫 *BidTrip Ticket*\n\n📍 *${getCategoryName(booking.category)}*\n📌 ${getLocationName(booking.location)}\n📅 ${booking.date}\n🕐 ${booking.time}\n💰 $${booking.total_price} USD\n\n🔑 Código: ${booking.qr_code}\n\n${language === 'en' ? 'Show this ticket at your experience!' : '¡Presenta este ticket en tu experiencia!'}`;
                  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(ticketText)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="mt-4 rounded-full bg-[#25D366] hover:bg-[#25D366]/90 text-white"
              >
                <MessageCircle size={18} className="mr-2" />
                {language === 'en' ? 'Send Ticket to WhatsApp' : 'Enviar Ticket a WhatsApp'}
              </Button>
            </div>
          )}

          {/* Payment Section */}
          {booking.status === 'pending_payment' && user?.role === 'tourist' && (
            <div className="p-6 border-t">
              <div className="bg-caribbean/10 rounded-xl p-3 mb-4 text-sm text-foreground/90">
                <p className="font-semibold mb-0.5">
                  {language === 'en' ? 'You only pay the reservation fee now' : 'Solo pagas la reserva ahora'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {language === 'en'
                    ? `The remaining $${(booking.provider_balance_due ?? 0).toFixed(2)} is paid directly to the provider on arrival.`
                    : `El resto ($${(booking.provider_balance_due ?? 0).toFixed(2)}) se lo pagas al proveedor al llegar.`}
                </p>
              </div>

              <Button
                data-testid="pay-now-btn"
                onClick={handlePayment}
                disabled={processingPayment}
                className="w-full h-14 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold text-lg"
              >
                <CreditCard className="mr-2" size={20} />
                {processingPayment
                  ? (language === 'en' ? 'Processing...' : 'Procesando...')
                  : (language === 'en'
                      ? `Reserve for $${(booking.reservation_fee ?? booking.total_price).toFixed(2)}`
                      : `Reservar por $${(booking.reservation_fee ?? booking.total_price).toFixed(2)}`)}
              </Button>
            </div>
          )}

          {/* Legacy partially_paid bookings (old model) */}
          {booking.status === 'partially_paid' && user?.role === 'tourist' && (
            <div className="p-6 border-t">
              <Button
                data-testid="pay-remaining-btn"
                onClick={handlePayment}
                disabled={processingPayment}
                className="w-full h-14 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold text-lg"
              >
                <CreditCard className="mr-2" size={20} />
                {language === 'en' ? `Pay Remaining $${(booking.total_price - booking.amount_paid).toFixed(2)}` : `Pagar Restante $${(booking.total_price - booking.amount_paid).toFixed(2)}`}
              </Button>
            </div>
          )}

          {/* Actions for confirmed bookings */}
          {isPaid && (
            <div className="p-6 border-t space-y-3">
              {/* Complete Experience Button */}
              {canComplete && (
                <Button
                  data-testid="complete-btn"
                  onClick={handleComplete}
                  disabled={submitting}
                  className="w-full h-14 rounded-full bg-foreground hover:bg-foreground/90 text-white font-semibold text-lg"
                >
                  <CheckCircle className="mr-2" size={20} />
                  {submitting 
                    ? (language === 'en' ? 'Processing...' : 'Procesando...')
                    : (language === 'en' ? 'Mark Experience as Completed' : 'Marcar Experiencia como Completada')}
                </Button>
              )}

              {/* Completed Badge */}
              {isCompleted && (
                <div className="p-4 bg-green-100 border-2 border-green-500 rounded-xl text-center">
                  <CheckCircle className="mx-auto text-green-600 mb-2" size={32} />
                  <p className="font-semibold text-green-800">
                    {language === 'en' ? 'Experience Completed!' : '¡Experiencia Completada!'}
                  </p>
                  <p className="text-sm text-green-600">
                    {language === 'en' 
                      ? 'Provider earnings have been released for withdrawal'
                      : 'Las ganancias del proveedor han sido liberadas para retiro'}
                  </p>
                </div>
              )}

              {booking.provider_phone && (
                <Button
                  data-testid="whatsapp-btn"
                  onClick={openWhatsApp}
                  className="w-full h-12 rounded-full bg-[#25D366] hover:bg-[#25D366]/90 text-white font-semibold"
                >
                  <MessageCircle className="mr-2" size={20} />
                  {language === 'en' ? 'Contact via WhatsApp' : 'Contactar por WhatsApp'}
                </Button>
              )}

              {canReview && (
                <Button
                  data-testid="review-btn"
                  onClick={() => setShowReviewDialog(true)}
                  variant="outline"
                  className="w-full h-12 rounded-full border-2 border-gold text-gold hover:bg-gold/10"
                >
                  <Star className="mr-2" size={20} />
                  {language === 'en' ? 'Leave a Review' : 'Dejar Reseña'}
                </Button>
              )}

              {review && (
                <div className="p-4 bg-gold/10 rounded-xl">
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={i < review.rating ? 'text-gold fill-gold' : 'text-muted'}
                      />
                    ))}
                    <span className="ml-2 text-sm text-muted-foreground">
                      {language === 'en' ? 'Your review' : 'Tu reseña'}
                    </span>
                  </div>
                  <p className="text-sm">{review.comment}</p>
                </div>
              )}
            </div>
          )}

          {/* Cancel Button */}
          {canCancel && (
            <div className="p-6 border-t">
              <Button
                data-testid="cancel-btn"
                onClick={() => setShowCancelDialog(true)}
                variant="ghost"
                className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <X className="mr-2" size={18} />
                {language === 'en' ? 'Cancel Booking' : 'Cancelar Reserva'}
              </Button>
              {booking.amount_paid > 0 && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  {language === 'en' 
                    ? '20% cancellation fee applies (10% to provider, 10% to platform)'
                    : 'Aplica 20% de cargo por cancelación (10% al proveedor, 10% a la plataforma)'}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Chat Section */}
        {canChat && (
          <BookingChat 
            bookingId={bookingId} 
            otherPartyName={user?.role === 'tourist' ? booking.provider_name : 'Tourist'}
          />
        )}

        {/* Review Dialog */}
        <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{language === 'en' ? 'Leave a Review' : 'Dejar Reseña'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              <div>
                <Label className="mb-2 block">{language === 'en' ? 'Rating' : 'Calificación'}</Label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewData({ ...reviewData, rating: star })}
                      className="focus:outline-none"
                    >
                      <Star
                        size={32}
                        className={`transition-colors ${
                          star <= reviewData.rating ? 'text-gold fill-gold' : 'text-muted hover:text-gold/50'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>{language === 'en' ? 'Comment' : 'Comentario'}</Label>
                <Textarea
                  value={reviewData.comment}
                  onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                  placeholder={language === 'en' ? 'Share your experience...' : 'Comparte tu experiencia...'}
                  className="min-h-[100px] mt-2"
                />
              </div>

              <Button
                onClick={handleSubmitReview}
                disabled={submitting}
                className="w-full h-12 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold"
              >
                {submitting 
                  ? (language === 'en' ? 'Submitting...' : 'Enviando...')
                  : (language === 'en' ? 'Submit Review' : 'Enviar Reseña')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle size={20} />
                {language === 'en' ? 'Cancel Booking' : 'Cancelar Reserva'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              {booking.amount_paid > 0 && (
                <div className="p-4 bg-destructive/10 rounded-xl space-y-2">
                  <p className="text-sm font-medium text-destructive">
                    {language === 'en' ? 'Cancellation Fee (20%)' : 'Cargo por Cancelación (20%)'}
                  </p>
                  <p className="font-bold text-destructive">${(booking.amount_paid * 0.2).toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'en' 
                      ? `You will be refunded: $${(booking.amount_paid * 0.8).toFixed(2)}`
                      : `Te será reembolsado: $${(booking.amount_paid * 0.8).toFixed(2)}`}
                  </p>
                </div>
              )}

              <div>
                <Label>{language === 'en' ? 'Reason (optional)' : 'Razón (opcional)'}</Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder={language === 'en' ? 'Why are you cancelling?' : '¿Por qué cancelas?'}
                  className="min-h-[80px] mt-2"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelDialog(false)}
                  className="flex-1 h-12 rounded-full"
                >
                  {language === 'en' ? 'Keep Booking' : 'Mantener Reserva'}
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={submitting}
                  className="flex-1 h-12 rounded-full bg-destructive hover:bg-destructive/90 text-white"
                >
                  {submitting 
                    ? (language === 'en' ? 'Cancelling...' : 'Cancelando...')
                    : (language === 'en' ? 'Confirm Cancel' : 'Confirmar Cancelación')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      </div>
    </MobileShell>
  );
}
